import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationService,
    TeacherPersonalInfoViewModel,
    FieldValidationErrors,
    Organization,
    TeacherPersonalInfoResponse,
    ExtendedFieldValidationErrors,
    Metadata,
    isFormFilled,
    TeacherSchoolComponent,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    FormFooterComponent as FormFooter,
    hasFailedInstantMatch,
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    submitForm,
    setRef,
    VerificationStepsEnum,
} from '@sheerid/jslib-nightly';

interface StepTeacherPersonalInfoComponentProps {
    verificationService: VerificationService;
    intl: any;
}

const StepTeacherPersonalInfo = ({ verificationService, intl }: StepTeacherPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as TeacherPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as TeacherPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateTeacherViewModel = (key: keyof TeacherPersonalInfoViewModel, value: string | Metadata) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    return (
        <div id="sid-step-teacher-personal-info" className="sid-l-container">


            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Email" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and email address must match what is on record with your school." />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Teaching-Only Offer" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a teacher at a K-12 school." />
                            &nbsp;
                            <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            <TeacherSchoolComponent
                value={(verificationService.viewModel as TeacherPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                placeholder={
                    intl.formatMessage({
                        id: 'schoolName',
                        defaultMessage: 'School name',
                    })
                }
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    placeholder={
                        intl.formatMessage({
                            id: 'firstName',
                            defaultMessage: 'First name',
                        })
                    }
                    onChange={
                        (newValue: string) => {
                            updateTeacherViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    placeholder={
                        intl.formatMessage({
                            id: 'lastName',
                            defaultMessage: 'Last name',
                        })
                    }
                    onChange={
                        (newValue: string) => {
                            updateTeacherViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                placeholder={
                    intl.formatMessage({
                        id: 'emailAddress',
                        defaultMessage: 'Email address (School-issued email required)',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectTeacherPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage id="verifyMyTeacherStatus" defaultMessage="VERIFY MY TEACHER STATUS"/>
                        }
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="studentInfoShared"
                    defaultMessage={'Information entered here will be used for verification purposes.'}
                />
            </FormFooter>
        </div>
    );
};

export const StepTeacherPersonalInfoComponent = injectIntl(StepTeacherPersonalInfo);
